import {
  BillingDocumentIntegrationStatus,
  BillingDocumentStatus,
} from "constants/billingDocuments";
import { BillingLedgerEntryStatus } from "constants/billingLedgerEntries";

export const billingDocumentAllowsReversal = billingDocument =>
  [
    BillingDocumentIntegrationStatus.AUTHORISED,
    BillingDocumentIntegrationStatus.PAID,
  ].includes(billingDocument.integrationStatus);

export const billingDocumentIntegrationIsActive = billingDocument =>
  [
    BillingDocumentIntegrationStatus.DRAFT,
    BillingDocumentIntegrationStatus.SUBMITTED,
    BillingDocumentIntegrationStatus.PAID,
    BillingDocumentIntegrationStatus.AUTHORISED,
  ].includes(billingDocument.integrationStatus);

export const expectingDeleteOnAcceptChanges = (
  billingDocument,
  ledgerEntriesByDocumentId,
) => {
  if (!billingDocument.hasFinancialChanges) {
    // shortcut as there are no financial changes to accept.
    return false;
  }

  // check if accepting change will delete this document,
  // don't allow if document has an active integration status
  const ledgerEntries = ledgerEntriesByDocumentId[billingDocument.id] || [];
  const temporaryLedgerEntries = ledgerEntries.filter(
    ({ ledgerEntry }) =>
      ledgerEntry.status === BillingLedgerEntryStatus.TEMPORARY,
  );
  const hasTemporaryLedgerEntries = temporaryLedgerEntries.length > 0;
  return !hasTemporaryLedgerEntries;
};

export const expectingDeleteWithActiveIntegrationOnAcceptChanges = (
  billingDocument,
  ledgerEntriesByDocumentId,
) => {
  const isExpectingDeleteOnAcceptChanges = expectingDeleteOnAcceptChanges(
    billingDocument,
    ledgerEntriesByDocumentId,
  );
  const hasActiveIntegration =
    billingDocumentIntegrationIsActive(billingDocument);
  return isExpectingDeleteOnAcceptChanges && hasActiveIntegration;
};

export const expectingDeleteWithoutActiveIntegrationOnAcceptChanges = (
  billingDocument,
  ledgerEntriesByDocumentId,
) => {
  const isExpectingDeleteOnAcceptChanges = expectingDeleteOnAcceptChanges(
    billingDocument,
    ledgerEntriesByDocumentId,
  );
  const hasActiveIntegration =
    billingDocumentIntegrationIsActive(billingDocument);
  return isExpectingDeleteOnAcceptChanges && !hasActiveIntegration;
};

export const integratedBillingDocumentDescription = billingDocument =>
  `${billingDocument.type} ${billingDocument.invoiceNumber} (${billingDocument.integrationStatus})`;

export const billingDocumentIsPaid = (billingDocument, payments) => {
  const { status, integrationStatus } = billingDocument;

  // Check status-based payment indicators
  const statusPaid =
    status === BillingDocumentStatus.PAID ||
    integrationStatus === BillingDocumentIntegrationStatus.PAID;

  // Check if total payments equal the document amount
  const totalPayments = payments.reduce(
    (sum, payment) => sum + (payment.amountCents || 0),
    0,
  );
  const documentAmountCents = billingDocument.totalAmountDollars * 100;
  const paymentsEqualAmount = totalPayments >= documentAmountCents;

  return statusPaid || paymentsEqualAmount;
};

export const billingDocumentIsApproved = billingDocument => {
  const { status } = billingDocument;
  return [
    BillingDocumentStatus.APPROVED,
    BillingDocumentStatus.SENT,
    BillingDocumentStatus.PAID,
    BillingDocumentStatus.NEEDS_ATTENTION, // This is here for now...
    BillingDocumentStatus.EXPORTED,
    BillingDocumentStatus.EXPORTING,
    BillingDocumentStatus.EXPORTED_WITH_WARNING,
    BillingDocumentStatus.EXPORT_ERROR,
  ].includes(status);
};
